
import React, { useCallback, useMemo } from 'react';
import { Grid, Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Draggable from 'react-draggable';
import _, { isNil, partial, remove } from 'lodash';

import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import { green, red } from '@mui/material/colors';

/**
 * @template T
 * @typedef {import('@dyl/pages/Tasks/edit/useTaskField').UseTaskField<T>} UseTaskField
 */

const useStyles = makeStyles(theme => ({
    itemContainer: {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
    },
}));

export default function DisplayOrderSection({ itemIndex }) {
    const classes = useStyles();

    /** @type {UseTaskField<Answer[]>} */
    const [answers] = useTaskField('items', itemIndex, 'answers');

    const orderedAnswers = useMemo(() => {
        return _(answers)
            .map((answer, index) => ({ answer, index }))
            .sortBy(({ answer }) => answer.display_order)
            .value();
    }, [answers]);

    return (
        <Grid container spacing={3}>
            {orderedAnswers.map(({ answer, index }) => (
                <Grid item xs={6} className={classes.itemContainer} key={index}>
                    <Option itemIndex={itemIndex} index={index} />
                </Grid>
            ))}
        </Grid>
    );
}

const useOptionStyles = makeStyles(theme => ({
    paper: {
        width: 56,
        height: 56,
        alignSelf: ({ displayOrder }) => displayOrder % 2 ? 'flex-end' : 'flex-start',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderStyle: 'solid',
        borderWidth: 2,
        borderColor: ({ isCorrect }) => isCorrect ? green[500] : red[500],
        transition: theme.transitions.create('box-shadow'),
        fontWeight: theme.typography.fontWeightBold,
        position: 'relative',
        cursor: 'grab',

        '&:not(:hover) $handle': {
            opacity: 0,
        },
    },
    dragging: {
        zIndex: 8,
        boxShadow: theme.shadows[8],
    },
    orderIndicator: {
        position: 'absolute',
        bottom: 0,
        left: '50%',
        transform: 'translate(-50%, 50%)',
        backgroundColor: 'green',
        borderRadius: 16,
        color: 'white',
        padding: '2px 8px',
    },
}));

function Option({ itemIndex, index }) {
    const useField = partial(useTaskField, 'items', itemIndex, 'answers', index);

    /** @type {UseTaskField<Answer[]>} */
    const [answers, setAnswers] = useTaskField('items', itemIndex, 'answers');
    const optionCount = answers.length;

    const [text] = useField('text');
    const [displayOrder] = useField('display_order');
    const [isCorrect] = useField('is_correct_answer');

    const classes = useOptionStyles({ displayOrder, isCorrect });

    const boxSize = 24 + 56;

    const bounds = useMemo(() => {
        const displayIndex = displayOrder - 1;

        return {
            top: -Math.floor(displayIndex / 2) * boxSize,
            left: -(displayIndex % 2) * boxSize,
            right: ((displayIndex + 1) % 2) * boxSize,
            bottom: (Math.floor(optionCount / 2) - Math.floor(displayIndex / 2) - 1) * boxSize,
        };
    }, [displayOrder, boxSize, optionCount]);

    const onDragEnd = useCallback(
        /** @type {import('react-draggable').DraggableProps['onStop']} */
        (event, data) => {
            const xOffset = Math.round(data.lastX / boxSize);
            const yOffset = Math.round(data.lastY / boxSize);

            let orderOffset = xOffset + (yOffset * 2);
            if (orderOffset === 0) return;
            if (orderOffset > 0) orderOffset++;

            setAnswers(prev => {
                const orderedIndexes = _(prev)
                    .map((answer, index) => ({ answer, index }))
                    .sortBy(({ answer }) => answer.display_order)
                    .map(({ index }) => index)
                    .value();

                const thisOrder = orderedIndexes.indexOf(index);

                orderedIndexes[thisOrder] = null;
                orderedIndexes.splice(thisOrder + orderOffset, 0, index);
                remove(orderedIndexes, isNil);

                return prev.map((a, i) => ({
                    ...a,
                    display_order: orderedIndexes.indexOf(i) + 1,
                }));
            });
        }, [boxSize, index, setAnswers]
    );

    return (
        <Draggable
            position={{ x: 0, y: 0 }}
            defaultClassNameDragging={classes.dragging}
            bounds={bounds}
            onStop={onDragEnd}
        >
            <Paper className={classes.paper} elevation={2}>
                {text}

                {isCorrect && (
                    <div className={classes.orderIndicator}>
                        {index + 1}
                    </div>
                )}
            </Paper>
        </Draggable>
    );
}